import React, { useEffect, useState, useCallback, useMemo, useContext } from 'react'
import { AuthContext } from '../../../services/context'
import { getAllUsers, deleteUser } from '../../../services/apiHelper'
import DetailView from '../../../dpe-usco/components/Details/DetailView'
import { Table } from '../../../dpe-usco'
import HeaderFilter from '../../../components/subComponents/HeaderFilter'
import { FilterBar } from '../../../components/FilterBar/FilterBar.jsx'
import Status from '../../../components/subComponents/Status.js'
import Tooltip from '../../../components/subComponents/Tooltip/Tooltip'
import MassActionToolbar from '../../../dpe-usco/components/MassActionToolbar/MassActionToolbar.jsx'
import { Button } from 'syngenta-react-components'
import trash from '../../../assets/icons/trash.svg'
import Warning from '../../../components/subComponents/Notifications/Warning.js'

const PAGE_SIZE = 1000

const UserList = () => {

  const { auth } = useContext(AuthContext)
  const [rawData, setRawData] = useState([])
  const [data, setData] = useState([])
  const [activeFilters, setActiveFilters] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [isEmptyStatus, setIsEmptyStatus] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [showDeleteWarning, setShowDeleteWarning] = useState(false)
  const [deletionProgress, setDeletionProgress] = useState({}) // { user_id: { status: 'pending' | 'in-progress' | 'success' | 'error' } }
  const [isDeleting, setIsDeleting] = useState(false)

  const applyFilters = useCallback((filters, sourceData) => {
    if (!filters || filters.length === 0) return sourceData
    return sourceData.filter(row => filters.every(f => {
      const rawVal = row[f.field]
      if (rawVal === undefined || rawVal === null) return false
      const strFilter = String(f.value).toLowerCase()
      // If field value is an array (roles / companies), match if any element includes filter
      if (Array.isArray(rawVal)) {
        return rawVal.some(item => String(item).toLowerCase().includes(strFilter))
      }
      return String(rawVal).toLowerCase().includes(strFilter)
    }))
  }, [])

  const fetchUsers = useCallback(async (skip = 0) => {
    if (skip === 0) setLoading(true)
    const { users: page } = await getAllUsers(PAGE_SIZE, skip)
    const normalized = page.map(u => ({ ...u, name: [u.first, u.last].filter(Boolean).join(' ') }))
    setRawData(prev => skip === 0 ? normalized : [...prev, ...normalized])
    if (skip === 0) {
      setIsEmptyStatus(normalized.length === 0)
      setLoading(false)
      setLoadingMore(page.length === PAGE_SIZE)
    }
    if (page.length === PAGE_SIZE) fetchUsers(skip + PAGE_SIZE)
    else setLoadingMore(false)
  }, [])

  const customSort = (dataSet, dataKey, sortBy) => {
    if (dataSet?.length && dataKey) {
      let newTableData = []
      if (dataKey === 'created' && sortBy === 'asc') newTableData = [...dataSet].sort((a, b) => new Date(a.created) - new Date(b.created))
      else if (dataKey === 'created') newTableData = [...dataSet].sort((a, b) => new Date(b.created) - new Date(a.created))
      else if (sortBy === 'asc') newTableData = [...dataSet].sort((a, b) => (a[dataKey]?.toString().toLowerCase() > b[dataKey]?.toString().toLowerCase() ? 1 : -1))
      else newTableData = [...dataSet].sort((a, b) => (a[dataKey]?.toString().toLowerCase() > b[dataKey]?.toString().toLowerCase() ? -1 : 1))
      return newTableData
    }
    return dataSet || []
  }

  const headerCell = (value, dataKey) => {
    const acendCallBack = () => { setData(prev => customSort(prev, dataKey, 'asc')) }
    const decendCallBack = () => { setData(prev => customSort(prev, dataKey, 'desc')) }

    return (
      <div key={value} className='cell df aic'>
        <span>{value}</span>
        {value !== 'Action' && <HeaderFilter
          dataKey={dataKey}
          descend={() => decendCallBack()}
          ascend={() => acendCallBack()}
        />}
      </div>
    )
  }

  const formatDate = dateString => {
    const date = new Date(dateString)
    const month = date.toLocaleString('en-US', { month: 'long' })
    const day = date.getDate()
    const year = date.getFullYear()
    let suffix = 'th'
    if (day === 1 || day === 21 || day === 31) {
      suffix = 'st'
    } else if (day === 2 || day === 22) {
      suffix = 'nd'
    } else if (day === 3 || day === 23) {
      suffix = 'rd'
    }
    const formattedDay = `${day}${suffix}`
    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    })
    return `${month} ${formattedDay} ${year}, ${time.toLowerCase()}`
  }

  const handleDeleteSelectedUsers = useCallback(async () => {
    if (!selectedRows.length) return
    setIsDeleting(true)
    // Kick off all deletions in parallel while updating status per user
    const promises = selectedRows.map(userId => {
      // mark in-progress
      setDeletionProgress(prev => ({ ...prev, [userId]: { status: 'in-progress' } }))
      return deleteUser(userId)
        .then(res => {
          setDeletionProgress(prev => ({ ...prev, [userId]: { status: res ? 'success' : 'error' } }))
        })
        .catch(() => {
          setDeletionProgress(prev => ({ ...prev, [userId]: { status: 'error' } }))
        })
    })
    await Promise.all(promises)
    await fetchUsers()
    setSelectedRows([])
    setIsDeleting(false)
    setShowDeleteWarning(false)
    setDeletionProgress({})
  }, [selectedRows, fetchUsers])

  // Initialize deletion progress entries when modal opens
  useEffect(() => {
    if (showDeleteWarning) {
      const init = {}
      selectedRows.forEach(id => { init[id] = { status: 'pending' } })
      setDeletionProgress(init)
    }
  }, [showDeleteWarning, selectedRows])

  const structure = {
    headers: [
      {
        value: 'Name',
        format: () => headerCell('Name', 'name')
      },
      {
        value: 'Roles',
        format: () => headerCell('Roles', 'roles')
      },
      {
        value: 'Companies',
        format: () => headerCell('Companies', 'companies')
      },
      {
        value: 'Email',
        format: () => headerCell('Email', 'email')
      },
      {
        value: 'Created At',
        format: () => headerCell('Created At', 'created')
      }
    ],
    dataKeys: [
      'name',
      'roles',
      'companies',
      'email',
      'created'
    ],
    rowFormat: {
      name: {
        format: value => {
          if (!value) {
            return <Status color={'red'} key={crypto.randomUUID()}>N/A</Status>
          }
          return (<div className=''>{value}</div>)
        }
      },
      roles: {
        format: value => {
          if (!value || !value.length) return <Status color={'red'} key={crypto.randomUUID()}>N/A</Status>
          // Uppercase transform for consistent display
          const rolesUpper = value.map(v => (v == null ? '' : v.toString().toUpperCase()))
          const first = rolesUpper[0]
          const total = rolesUpper.length
          const remaining = total - 1
          if (remaining === 0) {
            return (
              <div className='df aic fw-wrap'>
                <Status color={'yellow'} key={first}>{first}</Status>
              </div>
            )
          }
          const tooltip = (
            <div>
              {rolesUpper.map(v => <div key={v}>{v}</div>)}
            </div>
          )
          return (
            <div className='df aic fw-wrap'>
              <Tooltip content={tooltip} placement='top'>
                <Status color={'yellow'} key={first}>{first}</Status>
                <span className='ml4 fz11 c-gray-700 cursor-help'>and {remaining} more</span>
              </Tooltip>
            </div>
          )
        }
      },
      companies: {
        format: value => {
          if (!value || !value.length) return <Status color={'red'} key={crypto.randomUUID()}>N/A</Status>
          // Uppercase transform for consistent display
          const companiesUpper = value.map(v => (v == null ? '' : v.toString().toUpperCase()))
          const first = companiesUpper[0]
          const total = companiesUpper.length
          const remaining = total - 1
          if (remaining === 0) {
            return (
              <div className='df aic fw-wrap'>
                <Status color={'yellow'} key={first}>{first}</Status>
              </div>
            )
          }
          const tooltip = (
            <div>
              {companiesUpper.map(v => <div key={v}>{v}</div>)}
            </div>
          )
          return (
            <div className='df aic fw-wrap'>
              <Tooltip content={tooltip} placement='top'>
                <Status color={'yellow'} key={first}>{first}</Status>
                <span className='ml4 fz11 c-gray-700 cursor-help'>and {remaining} more</span>
              </Tooltip>
            </div>
          )
        }
      },
      created: {
        format: value => {
          return <div>{formatDate(value)}</div>
        }
      }
    }
  }

  // Distinct roles & companies lists (uppercase & sorted)
  const distinctRoles = useMemo(() => {
    const set = new Set()
    rawData.forEach(u => {
      if (Array.isArray(u.roles)) u.roles.forEach(r => { if (r) set.add(r.toString().toUpperCase()) })
    })
    return Array.from(set).sort()
  }, [rawData])

  const distinctCompanies = useMemo(() => {
    const set = new Set()
    rawData.forEach(u => {
      if (Array.isArray(u.companies)) u.companies.forEach(c => { if (c) set.add(c.toString().toUpperCase()) })
    })
    return Array.from(set).sort()
  }, [rawData])

  const fields = [
    { key: 'user_id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'roles', label: 'Role', values: distinctRoles },
    { key: 'companies', label: 'Company', values: distinctCompanies }
  ]

  useEffect(() => { fetchUsers() }, [fetchUsers])
  useEffect(() => { setData(applyFilters(activeFilters, rawData)) }, [activeFilters, rawData, applyFilters])

  return (
    <>
      {loading ? <div className="df aic jcc h175"> <div className='loader h80 w80' /> </div> :
        <><DetailView button={<FilterBar onChange={f => { setActiveFilters(f) }} fields={fields} />} title={'Users'} disableBack>
          <div className="mb20 mt20" style={{ position: 'relative' }}>
            {loadingMore && (
              <div className='loader h28 w28' style={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }} />
            )}
            <Table
              selectableRow={auth.role_type === 'admin'}
              rowSelectKey="user_id"
              onRowSelect={selectedRows => setSelectedRows(selectedRows)} structure={structure} data={data} statusComponent={isEmptyStatus ? <div>No users.</div> : null} />
          </div>
        </DetailView>
          {selectedRows.length > 0 && (
            <>
              <MassActionToolbar selectedRows={selectedRows} >
                <div>
                  <b>{selectedRows.length}</b> of <b>{rawData.length}</b> selected
                </div>
                <div>
                  <Button onClick={() => setShowDeleteWarning(true)}>
                    <div className='df aic jcc' style={{ gap: 5 }}>
                      <img width={15} src={trash} alt='Delete' />
                      Delete
                    </div>
                  </Button>
                </div>
              </MassActionToolbar>
              {
                showDeleteWarning && (
                  <Warning
                    handleDismissWarning={() => { if (!isDeleting) setShowDeleteWarning(false) }}
                    handleSubmit={handleDeleteSelectedUsers}
                    warningMessage2={`Are you sure want to delete ${selectedRows.length} users?`}
                    confirmationButtonText={'Delete'}
                    disabledButton={isDeleting}
                  >
                    {Object.keys(deletionProgress).length > 0 && (
                      <div className='mt10'>
                        <div className='fw600 mb10'>Progress</div>
                        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                          {selectedRows.map(id => {
                            const status = deletionProgress[id]?.status || 'pending'
                            const colorMap = {
                              pending: '#ccc',
                              'in-progress': '#0275d8',
                              success: '#28a745',
                              error: '#dc3545'
                            }
                            const labelMap = {
                              pending: 'Pending',
                              'in-progress': 'Deleting...',
                              success: 'Deleted',
                              error: 'Error'
                            }
                            return (
                              <div key={id} className='mb8'>
                                <div className='df jcsb fz12 mb4'>
                                  <span>User ID: {id}</span>
                                  <span>{labelMap[status]}</span>
                                </div>
                                <div style={{ background: '#eee', height: 6, borderRadius: 3 }}>
                                  <div style={{
                                    width: status === 'success' ? '100%' : status === 'error' ? '100%' : status === 'in-progress' ? '60%' : '0%',
                                    transition: 'width 0.4s',
                                    background: colorMap[status],
                                    height: '100%',
                                    borderRadius: 3
                                  }} />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </Warning>
                )
              }
            </>
          )}
        </>
      }
    </>
  )
}

export default UserList